import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageTransition from '@/components/layout/PageTransition';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Filter, 
  Plus, 
  Search,
  Grid, 
  List,
  Download,
  RefreshCw,
  Building,
  MapPin,
  Cpu
} from 'lucide-react';
import DeviceTable from '@/components/dashboard/DeviceTable';
import HierarchyFilter from '@/components/dashboard/HierarchyFilter';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useHierarchy } from '@/contexts/HierarchyContext';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

const Devices = () => {
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('all-devices');
  const [hierarchyFilters, setHierarchyFilters] = useState({});
  const navigate = useNavigate();
  const { userRole, hierarchyData } = useHierarchy();
  const { toast } = useToast();
  
  const [formState, setFormState] = useState({
    name: '',
    description: '',
    parentId: '',
    accessLevel: 'restricted',
  });
  
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };
  
  const handleHierarchyFilterChange = (filters: any) => {
    setHierarchyFilters(filters);
  };
  
  const handleSubmit = (e: React.FormEvent, entityType: string) => {
    e.preventDefault();
    
    toast({
      title: `New ${entityType} created`,
      description: `${formState.name} has been created successfully.`,
    });
    
    setFormState({
      name: '',
      description: '',
      parentId: '',
      accessLevel: 'restricted',
    });
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSelectChange = (name: string, value: string) => {
    setFormState(prev => ({ ...prev, [name]: value }));
  };
  
  const navigateToViewDevices = (filterType: string, filterId: string) => {
    navigate(`/view-devices?filterType=${filterType}&filterId=${filterId}`);
  };
  
  return (
    <PageTransition>
      <div className="section-padding">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Device Management</h1>
          <p className="text-muted-foreground">
            View and manage all devices in your mesh network
          </p>
        </div>
        
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div className="relative w-full md:w-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Search devices or users..." 
              className="pl-9 w-full md:w-[300px]" 
              value={searchQuery}
              onChange={handleSearch}
            />
          </div>
          
          <div className="flex items-center gap-3 w-full md:w-auto justify-between md:justify-start flex-wrap">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="h-9">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Entity
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel>Create New</DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                <Dialog>
                  <DialogTrigger asChild>
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      <Building className="h-4 w-4 mr-2" />
                      Organization
                    </DropdownMenuItem>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Organization</DialogTitle>
                      <DialogDescription>
                        Fill in the details to create a new organization.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={(e) => handleSubmit(e, 'organization')}>
                      <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                          <Label htmlFor="name">Organization Name</Label>
                          <Input 
                            id="name" 
                            name="name" 
                            value={formState.name} 
                            onChange={handleInputChange} 
                            placeholder="Enter organization name" 
                            required 
                          />
                        </div>
                        
                        <div className="grid gap-2">
                          <Label htmlFor="description">Description</Label>
                          <Input 
                            id="description" 
                            name="description" 
                            value={formState.description} 
                            onChange={handleInputChange} 
                            placeholder="Enter organization description" 
                          />
                        </div>
                        
                        <div className="grid gap-2">
                          <Label htmlFor="accessLevel">Security Classification</Label>
                          <Select 
                            value={formState.accessLevel} 
                            onValueChange={(value) => handleSelectChange('accessLevel', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select security level" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="top-secret">Top Secret</SelectItem>
                              <SelectItem value="secret">Secret</SelectItem>
                              <SelectItem value="confidential">Confidential</SelectItem>
                              <SelectItem value="restricted">Restricted</SelectItem>
                              <SelectItem value="unclassified">Unclassified</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <DialogFooter>
                        <Button type="submit">Create Organization</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
                
                <Dialog>
                  <DialogTrigger asChild>
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      <MapPin className="h-4 w-4 mr-2" />
                      Location
                    </DropdownMenuItem>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Location</DialogTitle>
                      <DialogDescription>
                        Fill in the details to create a new location.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={(e) => handleSubmit(e, 'location')}>
                      <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                          <Label htmlFor="name">Location Name</Label>
                          <Input 
                            id="name" 
                            name="name" 
                            value={formState.name} 
                            onChange={handleInputChange} 
                            placeholder="Enter location name" 
                            required 
                          />
                        </div>
                        
                        <div className="grid gap-2">
                          <Label htmlFor="description">Description</Label>
                          <Input 
                            id="description" 
                            name="description" 
                            value={formState.description} 
                            onChange={handleInputChange} 
                            placeholder="Enter location description" 
                          />
                        </div>
                        
                        <div className="grid gap-2">
                          <Label htmlFor="parentId">Department</Label>
                          <Select 
                            value={formState.parentId} 
                            onValueChange={(value) => handleSelectChange('parentId', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select department" />
                            </SelectTrigger>
                            <SelectContent>
                              {hierarchyData.departments.map(dept => (
                                <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="grid gap-2">
                          <Label htmlFor="accessLevel">Security Classification</Label>
                          <Select 
                            value={formState.accessLevel} 
                            onValueChange={(value) => handleSelectChange('accessLevel', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select security level" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="top-secret">Top Secret</SelectItem>
                              <SelectItem value="secret">Secret</SelectItem>
                              <SelectItem value="confidential">Confidential</SelectItem>
                              <SelectItem value="restricted">Restricted</SelectItem>
                              <SelectItem value="unclassified">Unclassified</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <DialogFooter>
                        <Button type="submit">Create Location</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
                
                <Dialog>
                  <DialogTrigger asChild>
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      <Cpu className="h-4 w-4 mr-2" />
                      Device Type
                    </DropdownMenuItem>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Device Type</DialogTitle>
                      <DialogDescription>
                        Fill in the details to create a new device type.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={(e) => handleSubmit(e, 'deviceType')}>
                      <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                          <Label htmlFor="name">Type Name</Label>
                          <Input 
                            id="name" 
                            name="name" 
                            value={formState.name} 
                            onChange={handleInputChange} 
                            placeholder="Enter device type name" 
                            required 
                          />
                        </div>
                        
                        <div className="grid gap-2">
                          <Label htmlFor="description">Description</Label>
                          <Input 
                            id="description" 
                            name="description" 
                            value={formState.description} 
                            onChange={handleInputChange} 
                            placeholder="Enter device type description" 
                          />
                        </div>
                        
                        <div className="grid gap-2">
                          <Label htmlFor="accessLevel">Default Security Classification</Label>
                          <Select 
                            value={formState.accessLevel} 
                            onValueChange={(value) => handleSelectChange('accessLevel', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select security level" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="top-secret">Top Secret</SelectItem>
                              <SelectItem value="secret">Secret</SelectItem>
                              <SelectItem value="confidential">Confidential</SelectItem>
                              <SelectItem value="restricted">Restricted</SelectItem>
                              <SelectItem value="unclassified">Unclassified</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <DialogFooter>
                        <Button type="submit">Create Device Type</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
                
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/add-device')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Device
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-9">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuLabel>Filter By</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <div className="flex items-center justify-between w-full">
                    <span>Status</span>
                    <Select>
                      <SelectTrigger className="w-24 h-8">
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="online">Online</SelectItem>
                        <SelectItem value="offline">Offline</SelectItem>
                        <SelectItem value="warning">Warning</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <div className="flex items-center justify-between w-full">
                    <span>Type</span>
                    <Select>
                      <SelectTrigger className="w-24 h-8">
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="gateway">Gateway</SelectItem>
                        <SelectItem value="sensor">Sensor</SelectItem>
                        <SelectItem value="actuator">Actuator</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem>Apply Filters</DropdownMenuItem>
                <DropdownMenuItem>Reset Filters</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <HierarchyFilter onFilterChange={handleHierarchyFilterChange} />
            
            <div className="flex items-center border rounded-md overflow-hidden">
              <Button 
                variant={viewMode === 'list' ? "default" : "ghost"} 
                size="sm" 
                className="h-9 rounded-none"
                onClick={() => setViewMode('list')}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button 
                variant={viewMode === 'grid' ? "default" : "ghost"} 
                size="sm" 
                className="h-9 rounded-none"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="h-4 w-4" />
              </Button>
            </div>
            
            <Button variant="outline" size="sm" className="h-9">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            
            <Button variant="outline" size="sm" className="h-9">
              <RefreshCw className="h-4 w-4" />
            </Button>
            
            <Button size="sm" className="h-9" onClick={() => navigate('/add-device')}>
              <Plus className="h-4 w-4 mr-2" />
              Add Device
            </Button>
          </div>
        </div>
        
        <Tabs defaultValue="all-devices" className="mb-6" onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all-devices">All Devices</TabsTrigger>
            <TabsTrigger value="by-organization">By Organization</TabsTrigger>
            <TabsTrigger value="by-location">By Location</TabsTrigger>
            <TabsTrigger value="by-type">By Device Type</TabsTrigger>
          </TabsList>
          
          <TabsContent value="all-devices" className="mt-4">
            <div className="glass rounded-xl p-5">
              <DeviceTable 
                searchQuery={searchQuery} 
                hierarchyFilters={hierarchyFilters}
              />
            </div>
          </TabsContent>
          
          <TabsContent value="by-organization" className="mt-4">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {hierarchyData.organizations.map(org => (
                <Card key={org.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      {org.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">{org.description || "Organization in the network"}</p>
                    <Button 
                      variant="outline" 
                      className="w-full" 
                      onClick={() => navigateToViewDevices('organization', org.id)}
                    >
                      View Devices
                    </Button>
                  </CardContent>
                </Card>
              ))}
              
              <Dialog>
                <DialogTrigger asChild>
                  <Card className="border-dashed cursor-pointer hover:bg-muted/50 transition-colors">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2 text-muted-foreground">
                        <Plus className="h-4 w-4" />
                        Add Organization
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">Create a new organization to group devices</p>
                    </CardContent>
                  </Card>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Organization</DialogTitle>
                    <DialogDescription>
                      Fill in the details to create a new organization.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={(e) => handleSubmit(e, 'organization')}>
                    <div className="grid gap-4 py-4">
                      <div className="grid gap-2">
                        <Label htmlFor="name">Organization Name</Label>
                        <Input 
                          id="name" 
                          name="name" 
                          value={formState.name} 
                          onChange={handleInputChange} 
                          placeholder="Enter organization name" 
                          required 
                        />
                      </div>
                      
                      <div className="grid gap-2">
                        <Label htmlFor="description">Description</Label>
                        <Input 
                          id="description" 
                          name="description" 
                          value={formState.description} 
                          onChange={handleInputChange} 
                          placeholder="Enter organization description" 
                        />
                      </div>
                      
                      <div className="grid gap-2">
                        <Label htmlFor="accessLevel">Security Classification</Label>
                        <Select 
                          value={formState.accessLevel} 
                          onValueChange={(value) => handleSelectChange('accessLevel', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select security level" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="top-secret">Top Secret</SelectItem>
                            <SelectItem value="secret">Secret</SelectItem>
                            <SelectItem value="confidential">Confidential</SelectItem>
                            <SelectItem value="restricted">Restricted</SelectItem>
                            <SelectItem value="unclassified">Unclassified</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button type="submit">Create Organization</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>
          
          <TabsContent value="by-location" className="mt-4">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {hierarchyData.locations.map(loc => (
                <Card key={loc.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      {loc.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">{loc.description || "Location in the network"}</p>
                    <Button 
                      variant="outline" 
                      className="w-full" 
                      onClick={() => navigateToViewDevices('location', loc.id)}
                    >
                      View Devices
                    </Button>
                  </CardContent>
                </Card>
              ))}
              
              <Dialog>
                <DialogTrigger asChild>
                  <Card className="border-dashed cursor-pointer hover:bg-muted/50 transition-colors">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2 text-muted-foreground">
                        <Plus className="h-4 w-4" />
                        Add Location
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">Create a new location for your devices</p>
                    </CardContent>
                  </Card>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Location</DialogTitle>
                    <DialogDescription>
                      Fill in the details to create a new location.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={(e) => handleSubmit(e, 'location')}>
                    <div className="grid gap-4 py-4">
                      <div className="grid gap-2">
                        <Label htmlFor="name">Location Name</Label>
                        <Input 
                          id="name" 
                          name="name" 
                          value={formState.name} 
                          onChange={handleInputChange} 
                          placeholder="Enter location name" 
                          required 
                        />
                      </div>
                      
                      <div className="grid gap-2">
                        <Label htmlFor="description">Description</Label>
                        <Input 
                          id="description" 
                          name="description" 
                          value={formState.description} 
                          onChange={handleInputChange} 
                          placeholder="Enter location description" 
                        />
                      </div>
                      
                      <div className="grid gap-2">
                        <Label htmlFor="parentId">Department</Label>
                        <Select 
                          value={formState.parentId} 
                          onValueChange={(value) => handleSelectChange('parentId', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select department" />
                          </SelectTrigger>
                          <SelectContent>
                            {hierarchyData.departments.map(dept => (
                              <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="grid gap-2">
                        <Label htmlFor="accessLevel">Security Classification</Label>
                        <Select 
                          value={formState.accessLevel} 
                          onValueChange={(value) => handleSelectChange('accessLevel', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select security level" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="top-secret">Top Secret</SelectItem>
                            <SelectItem value="secret">Secret</SelectItem>
                            <SelectItem value="confidential">Confidential</SelectItem>
                            <SelectItem value="restricted">Restricted</SelectItem>
                            <SelectItem value="unclassified">Unclassified</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button type="submit">Create Location</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>
          
          <TabsContent value="by-type" className="mt-4">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {['Gateway', 'Sensor', 'Actuator', 'Camera'].map(type => (
                <Card key={type}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Cpu className="h-4 w-4" />
                      {type}s
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">
                      {type === 'Gateway' && 'Core network devices for communication'}
                      {type === 'Sensor' && 'Data collection devices'}
                      {type === 'Actuator' && 'Control and automation devices'}
                      {type === 'Camera' && 'Visual monitoring devices'}
                    </p>
                    <Button 
                      variant="outline" 
                      className="w-full" 
                      onClick={() => navigateToViewDevices('deviceType', type)}
                    >
                      View Devices
                    </Button>
                  </CardContent>
                </Card>
              ))}
              
              <Dialog>
                <DialogTrigger asChild>
                  <Card className="border-dashed cursor-pointer hover:bg-muted/50 transition-colors">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2 text-muted-foreground">
                        <Plus className="h-4 w-4" />
                        Add Device Type
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">Create a new device type category</p>
                    </CardContent>
                  </Card>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Device Type</DialogTitle>
                    <DialogDescription>
                      Fill in the details to create a new device type.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={(e) => handleSubmit(e, 'deviceType')}>
                    <div className="grid gap-4 py-4">
                      <div className="grid gap-2">
                        <Label htmlFor="name">Type Name</Label>
                        <Input 
                          id="name" 
                          name="name" 
                          value={formState.name} 
                          onChange={handleInputChange} 
                          placeholder="Enter device type name" 
                          required 
                        />
                      </div>
                      
                      <div className="grid gap-2">
                        <Label htmlFor="description">Description</Label>
                        <Input 
                          id="description" 
                          name="description" 
                          value={formState.description} 
                          onChange={handleInputChange} 
                          placeholder="Enter device type description" 
                        />
                      </div>
                      
                      <div className="grid gap-2">
                        <Label htmlFor="accessLevel">Default Security Classification</Label>
                        <Select 
                          value={formState.accessLevel} 
                          onValueChange={(value) => handleSelectChange('accessLevel', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select security level" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="top-secret">Top Secret</SelectItem>
                            <SelectItem value="secret">Secret</SelectItem>
                            <SelectItem value="confidential">Confidential</SelectItem>
                            <SelectItem value="restricted">Restricted</SelectItem>
                            <SelectItem value="unclassified">Unclassified</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <DialogFooter>
                      <Button type="submit">Create Device Type</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PageTransition>
  );
};

export default Devices;
